import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import {
  Button,
  Spinner,
  Input,
  Textarea,
} from '../../components/design-system';
import { AdminPage } from './AdminPage';
import { UserDetailModal } from './UserDetailModal';
import { BulkDialog, type BulkMode } from '../../components/admin/BulkDialog';
import { lastSeenLabel, usersToCsv, type DirectorySort, type DirectoryUser } from '../../lib/adminUsers';
import type { UserRole, HostProfile } from '../../types';

interface HostFormState {
  headline: string;
  city: string;
  country: string;
  specialties: string;
  bio: string;
  taste_notes: string;
  active: boolean;
}

const EMPTY_HOST_FORM: HostFormState = {
  headline: '', city: '', country: '', specialties: '', bio: '', taste_notes: '', active: true,
};

function HostEditor({ userId, onClose }: { userId: string; onClose: () => void }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [existingId, setExistingId] = useState<string | null>(null);
  const [form, setForm] = useState<HostFormState>(EMPTY_HOST_FORM);
  const [missedTrips, setMissedTrips] = useState(0);
  const [suspendedAt, setSuspendedAt] = useState<string | null>(null);
  const [reactivating, setReactivating] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    void supabase
      .from('host_profiles')
      .select('*')
      .eq('profile_id', userId)
      .maybeSingle()
      .then(({ data, error: fetchError }) => {
        if (!active) return;
        if (fetchError) { setError(fetchError.message); }
        else if (data) {
          const hp = data as HostProfile;
          setExistingId(hp.id);
          setForm({
            headline: hp.headline ?? '',
            city: hp.city ?? '',
            country: hp.country ?? '',
            specialties: (hp.specialties ?? []).join(', '),
            bio: hp.bio ?? '',
            taste_notes: hp.taste_notes ?? '',
            active: hp.active,
          });
          setMissedTrips(hp.consecutive_missed_trips ?? 0);
          setSuspendedAt(hp.suspended_for_missed_trips_at ?? null);
        }
        setLoading(false);
      });
    return () => { active = false; };
  }, [userId]);

  async function handleReactivate() {
    if (!existingId) return;
    setReactivating(true);
    const { error: reactivateError } = await supabase
      .from('host_profiles')
      .update({ consecutive_missed_trips: 0, suspended_for_missed_trips_at: null, updated_at: new Date().toISOString() })
      .eq('id', existingId);
    setReactivating(false);
    if (reactivateError) { setError(reactivateError.message); return; }
    setMissedTrips(0);
    setSuspendedAt(null);
    setToast('Reactivated');
    setTimeout(() => setToast(null), 2000);
  }

  function update<K extends keyof HostFormState>(key: K, value: HostFormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    const specialtiesArray = form.specialties.split(',').map((s) => s.trim()).filter(Boolean);
    const payload = {
      profile_id: userId, headline: form.headline, city: form.city, country: form.country,
      specialties: specialtiesArray, bio: form.bio, taste_notes: form.taste_notes,
      active: form.active, updated_at: new Date().toISOString(),
    };
    const { error: saveError, data } = existingId
      ? await supabase.from('host_profiles').update(payload).eq('id', existingId).select('id').maybeSingle()
      : await supabase.from('host_profiles').insert(payload).select('id').maybeSingle();
    setSaving(false);
    if (saveError) { setError(saveError.message); return; }
    if (data && !existingId) setExistingId((data as { id: string }).id);
    setToast('Saved');
    setTimeout(() => setToast(null), 2000);
  }

  if (loading) return <div className="bg-app-bg rounded-xl p-4 flex justify-center"><Spinner size="sm" /></div>;

  return (
    <div className="bg-app-bg rounded-xl p-4">
      {suspendedAt ? (
        <div className="mb-4 rounded-lg border border-crimson/30 bg-crimson/10 px-3 py-2.5 flex items-center justify-between gap-3">
          <div className="text-xs text-crimson">
            <span className="font-medium">Suspended from matching</span> — 3 consecutive silent misses (no accept, no pass), since {new Date(suspendedAt).toLocaleDateString()}.
          </div>
          <Button size="sm" variant="ghost" onClick={() => void handleReactivate()} disabled={reactivating} className="shrink-0">
            {reactivating ? <Spinner size="sm" /> : 'Reactivate'}
          </Button>
        </div>
      ) : missedTrips > 0 ? (
        <p className="mb-4 text-xs text-app-muted">{missedTrips}/3 consecutive missed trips (silent, no response) — suspends from matching at 3.</p>
      ) : null}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input label="Headline" value={form.headline} onChange={(e) => update('headline', e.target.value)} disabled={saving} />
        <Input label="Specialties (comma-separated)" value={form.specialties} onChange={(e) => update('specialties', e.target.value)} disabled={saving} />
        <Input label="City" value={form.city} onChange={(e) => update('city', e.target.value)} disabled={saving} />
        <Input label="Country" value={form.country} onChange={(e) => update('country', e.target.value)} disabled={saving} />
        <div className="sm:col-span-2">
          <Textarea label="Bio" value={form.bio} onChange={(e) => update('bio', e.target.value)} disabled={saving} rows={3} />
        </div>
        <div className="sm:col-span-2">
          <Textarea label="Taste notes" value={form.taste_notes} onChange={(e) => update('taste_notes', e.target.value)} disabled={saving} rows={3} />
        </div>
        <label className="flex items-center gap-2 text-sm text-app-fg-2 sm:col-span-2">
          <input type="checkbox" checked={form.active} onChange={(e) => update('active', e.target.checked)} disabled={saving} className="rounded border-app-border text-gold" />
          Active
        </label>
      </div>
      {error && <p className="mt-3 text-xs text-crimson">{error}</p>}
      <div className="mt-4 flex items-center gap-3">
        <Button size="sm" onClick={() => void handleSave()} disabled={saving}>
          {saving ? <Spinner size="sm" className="text-ink" /> : 'Save host profile'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose} disabled={saving}>Done</Button>
        {toast && <span className="text-xs text-accent font-medium">{toast}</span>}
      </div>
    </div>
  );
}

const ROLE_COLORS: Record<string, string> = {
  traveler: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  host: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  admin: 'bg-violet/10 text-violet border-violet/20',
};

const PAGE_SIZE = 25;

const ROLE_FILTERS: { value: '' | UserRole; label: string }[] = [
  { value: '', label: 'All roles' },
  { value: 'traveler', label: 'Travelers' },
  { value: 'host', label: 'Hosts' },
  { value: 'admin', label: 'Admins' },
];

const STATUS_FILTERS = [
  { value: '', label: 'Everyone' },
  { value: 'active', label: 'Active' },
  { value: 'no_terms', label: 'Terms not accepted' },
  { value: 'open_trip', label: 'Has an open trip' },
  { value: 'deleted', label: 'Deleted' },
] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number]['value'];

function money(cents: number): string {
  return cents > 0 ? `€${(cents / 100).toFixed(2)}` : '—';
}

type DirectoryArgs = { search: string; role: '' | UserRole; status: StatusFilter; tag: string; sort: DirectorySort; dir: 'asc' | 'desc' };

const MAX_SELECTED = 500;

function directoryQuery(a: DirectoryArgs, limit: number, offset: number) {
  return supabase.rpc('admin_list_users', {
    p_search: a.search.trim() || null,
    p_role: a.role || null,
    p_status: a.status || null,
    p_sort: a.sort,
    p_dir: a.dir,
    p_limit: limit,
    p_offset: offset,
    p_tag: a.tag || null,
  });
}

export function AdminUsers() {
  const { user } = useAuth();
  const [rows, setRows] = useState<DirectoryUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [openEditor, setOpenEditor] = useState<string | null>(null);
  const [detailUser, setDetailUser] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<'' | UserRole>('');
  const [status, setStatus] = useState<StatusFilter>('');
  const [tag, setTag] = useState('');
  const [allTags, setAllTags] = useState<{ tag: string; people: number }[]>([]);
  // People ticked in the list; kept across pages, up to MAX_SELECTED.
  const [selected, setSelected] = useState<Map<string, DirectoryUser>>(new Map());
  const [bulk, setBulk] = useState<BulkMode | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [sort, setSort] = useState<DirectorySort>('joined');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const requestId = useRef(0);

  // Typing waits a moment before it asks the server.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    const mine = ++requestId.current;
    setLoading(true);
    setLoadError(false);
    const { data, error } = await directoryQuery({ search, role, status, tag, sort, dir }, PAGE_SIZE, page * PAGE_SIZE);
    if (mine !== requestId.current) return; // a newer request replaced this one
    if (error) {
      console.error('admin_list_users failed:', error);
      setLoadError(true);
      setRows([]);
      setTotal(0);
    } else {
      const list = (data ?? []) as DirectoryUser[];
      setRows(list);
      setTotal(list[0]?.total_count ?? 0);
    }
    setLoading(false);
  }, [search, role, status, tag, sort, dir, page]);

  useEffect(() => {
    if (!user) return;
    void load();
  }, [user, load]);

  const loadTags = useCallback(async () => {
    const { data } = await supabase.rpc('admin_all_tags');
    setAllTags((data as { tag: string; people: number }[] | null) ?? []);
  }, []);
  useEffect(() => { if (user) void loadTags(); }, [user, loadTags]);

  function toggleSelected(u: DirectoryUser) {
    setSelected((cur) => {
      const next = new Map(cur);
      if (next.has(u.id)) next.delete(u.id);
      else if (next.size < MAX_SELECTED) next.set(u.id, u);
      return next;
    });
  }
  const pageAllSelected = rows.length > 0 && rows.every((u) => selected.has(u.id));
  function togglePage() {
    setSelected((cur) => {
      const next = new Map(cur);
      if (pageAllSelected) rows.forEach((u) => next.delete(u.id));
      else for (const u of rows) { if (next.size < MAX_SELECTED) next.set(u.id, u); }
      return next;
    });
  }

  function exportSelected() {
    const blob = new Blob([usersToCsv([...selected.values()])], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sekretly-users-selected-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleSort(key: DirectorySort) {
    if (sort === key) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSort(key); setDir(key === 'name' || key === 'email' ? 'asc' : 'desc'); }
    setPage(0);
  }

  // Everything the filters match, not only the page on screen.
  async function handleExport() {
    setExporting(true);
    try {
      const all: DirectoryUser[] = [];
      for (let offset = 0; offset < 5000; offset += 200) {
        const { data, error } = await directoryQuery({ search, role, status, tag, sort, dir }, 200, offset);
        if (error) throw error;
        const chunk = (data ?? []) as DirectoryUser[];
        all.push(...chunk);
        if (chunk.length < 200) break;
      }
      const blob = new Blob([usersToCsv(all)], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sekretly-users-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('users export failed:', err);
      setLoadError(true);
    }
    setExporting(false);
  }

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const filtering = Boolean(search || role || status || tag);
  const selectClass = 'rounded-full border border-app-border bg-app-bg px-3 py-2.5 text-sm text-app-fg focus:border-gold focus:outline-none';

  return (
    <AdminPage
      title="Users"
      description={loading && rows.length === 0 ? ' ' : `${total} ${filtering ? 'matching ' : ''}user${total !== 1 ? 's' : ''}`}
      actions={<Button size="sm" variant="secondary" onClick={() => void handleExport()} disabled={exporting || total === 0}>
        {exporting ? <Spinner size="sm" /> : 'Export CSV'}
      </Button>}
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-80">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-app-muted pointer-events-none">
            <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
          </svg>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search name, email or user ID…"
            aria-label="Search users"
            className="w-full rounded-full border border-app-border bg-app-bg pl-9 pr-4 py-2.5 text-sm text-app-fg placeholder:text-app-muted focus:border-gold focus:outline-none"
          />
        </div>
        <select aria-label="Role" value={role} onChange={(e) => { setRole(e.target.value as '' | UserRole); setPage(0); }} className={selectClass}>
          {ROLE_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
        <select aria-label="Status" value={status} onChange={(e) => { setStatus(e.target.value as StatusFilter); setPage(0); }} className={selectClass}>
          {STATUS_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
        {allTags.length > 0 && (
          <select aria-label="Tag" value={tag} onChange={(e) => { setTag(e.target.value); setPage(0); }} className={selectClass}>
            <option value="">Any tag</option>
            {allTags.map((t) => <option key={t.tag} value={t.tag}>{t.tag} ({t.people})</option>)}
          </select>
        )}
        {filtering && (
          <Button size="sm" variant="ghost" onClick={() => { setSearchInput(''); setSearch(''); setRole(''); setStatus(''); setTag(''); setPage(0); }}>
            Clear filters
          </Button>
        )}
      </div>

      {notice && <p role="status" className="mb-3 text-sm text-emerald-500">{notice}</p>}
      {selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-gold/30 bg-gold/10 px-4 py-2.5 text-sm">
          <span className="font-medium text-app-fg">{selected.size} selected{selected.size >= MAX_SELECTED ? ' (the most you can pick)' : ''}</span>
          <Button size="sm" variant="secondary" onClick={exportSelected}>Export CSV</Button>
          <Button size="sm" variant="secondary" onClick={() => { setNotice(null); setBulk('add_tag'); }}>Add tag</Button>
          <Button size="sm" variant="secondary" onClick={() => { setNotice(null); setBulk('remove_tag'); }}>Remove tag</Button>
          <Button size="sm" variant="secondary" onClick={() => { setNotice(null); setBulk('push'); }}>Send push</Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Map())}>Clear</Button>
        </div>
      )}

      {loadError && (
        <p role="alert" className="mb-3 text-sm text-crimson">We couldn't load the users. Try again in a moment.</p>
      )}

      {loading && rows.length === 0 ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : (
        <div className={`overflow-x-auto rounded-xl border border-app-border bg-app-card shadow-sm ${loading ? 'opacity-60' : ''}`}>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-app-border bg-app-bg">
                <th className="pl-4 pr-0 py-3 w-8">
                  <input type="checkbox" aria-label="Select everyone on this page" checked={pageAllSelected} onChange={togglePage} />
                </th>
                <SortHeader label="Person" sortKey="name" current={sort} dir={dir} onClick={handleSort} />
                <th className="text-left px-4 py-3 font-medium text-app-muted hidden md:table-cell">Roles</th>
                <SortHeader label="Last seen" sortKey="last_seen" current={sort} dir={dir} onClick={handleSort} className="hidden lg:table-cell" />
                <SortHeader label="Trips" sortKey="trips" current={sort} dir={dir} onClick={handleSort} className="hidden md:table-cell" />
                <SortHeader label="Paid" sortKey="paid" current={sort} dir={dir} onClick={handleSort} className="hidden lg:table-cell" />
                <SortHeader label="Joined" sortKey="joined" current={sort} dir={dir} onClick={handleSort} />
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-app-muted text-xs">
                  {filtering ? 'No users match these filters.' : 'No users found.'}
                </td></tr>
              )}
              {rows.map((u) => {
                const userRoles = u.roles ?? [u.role];
                return (
                  <Fragment key={u.id}>
                    <tr className="border-b border-app-border hover:bg-app-bg/50">
                      <td className="pl-4 pr-0 py-3 w-8">
                        <input type="checkbox" aria-label={`Select ${u.full_name ?? u.email ?? 'user'}`} checked={selected.has(u.id)} onChange={() => toggleSelected(u)} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-full overflow-hidden avatar-host flex items-center justify-center text-cream text-sm font-semibold shrink-0">
                            {u.avatar_url
                              ? <img src={u.avatar_url} alt="" className="w-full h-full object-cover" />
                              : (u.full_name ?? '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <button
                              onClick={() => setDetailUser(u.id)}
                              className="text-left text-app-fg hover:text-gold transition-colors font-medium truncate max-w-[16rem] block"
                            >
                              {u.full_name ?? <span className="text-app-muted italic">No name</span>}
                            </button>
                            <p className="text-xs text-app-muted truncate max-w-[16rem]">{u.email ?? 'No email'}</p>
                            <div className="flex flex-wrap gap-1 mt-1 md:hidden">
                              {userRoles.map((r) => <span key={r} className={`text-[10px] font-medium rounded-full px-1.5 py-0.5 border ${ROLE_COLORS[r]}`}>{r}</span>)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <div className="flex flex-wrap items-center gap-1.5">
                          {userRoles.map((r) => (
                            <span key={r} className={`text-xs font-medium rounded-full px-2 py-0.5 border ${ROLE_COLORS[r]}`}>{r}</span>
                          ))}
                          {u.deleted_at && <span className="text-xs font-medium rounded-full px-2 py-0.5 border border-crimson/30 bg-crimson/10 text-crimson">Deleted</span>}
                          {!u.deleted_at && !u.terms_accepted_at && <span className="text-xs font-medium rounded-full px-2 py-0.5 border border-app-border text-app-muted">No terms yet</span>}
                          {u.open_trips > 0 && <span className="text-xs font-medium rounded-full px-2 py-0.5 border border-emerald-500/30 bg-emerald-500/10 text-emerald-500">Trip open</span>}
                          {(u.tags ?? []).map((t) => <span key={t} className="text-xs rounded-full px-2 py-0.5 bg-app-bg border border-app-border text-app-fg">#{t}</span>)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-app-muted text-xs hidden lg:table-cell">{lastSeenLabel(u.last_sign_in_at)}</td>
                      <td className="px-4 py-3 text-app-fg hidden md:table-cell">{u.trips || '—'}</td>
                      <td className="px-4 py-3 text-app-fg hidden lg:table-cell">{money(u.paid_cents)}</td>
                      <td className="px-4 py-3 text-app-muted text-xs whitespace-nowrap">
                        {new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {userRoles.includes('host') && (
                          <Button size="sm" variant="ghost" className="px-2 py-1 text-xs"
                            onClick={() => setOpenEditor((cur) => (cur === u.id ? null : u.id))}>
                            {openEditor === u.id ? 'Close host editor' : 'Edit host'}
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="px-2 py-1 text-xs" onClick={() => setDetailUser(u.id)}>Open</Button>
                      </td>
                    </tr>
                    {userRoles.includes('host') && openEditor === u.id && (
                      <tr className="border-b border-app-border bg-app-bg/40">
                        <td colSpan={8} className="px-4 py-3">
                          <HostEditor userId={u.id} onClose={() => setOpenEditor(null)} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between text-sm text-app-muted">
          <span>Page {page + 1} of {pages}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0 || loading}>Previous</Button>
            <Button size="sm" variant="secondary" onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1 || loading}>Next</Button>
          </div>
        </div>
      )}

      {bulk && (
        <BulkDialog
          mode={bulk}
          people={[...selected.values()]}
          onClose={() => setBulk(null)}
          onDone={(message) => {
            setBulk(null);
            setNotice(message);
            if (bulk !== 'push') { void load(); void loadTags(); }
          }}
        />
      )}

      {detailUser && (
        <UserDetailModal userId={detailUser} onClose={() => setDetailUser(null)} onChanged={() => { void load(); void loadTags(); }} />
      )}
    </AdminPage>
  );
}

function SortHeader({ label, sortKey, current, dir, onClick, className = '' }: {
  label: string;
  sortKey: DirectorySort;
  current: DirectorySort;
  dir: 'asc' | 'desc';
  onClick: (key: DirectorySort) => void;
  className?: string;
}) {
  const active = current === sortKey;
  return (
    <th className={`text-left px-4 py-3 font-medium text-app-muted ${className}`}>
      <button
        onClick={() => onClick(sortKey)}
        className={`flex items-center gap-1 hover:text-app-fg transition-colors ${active ? 'text-app-fg' : ''}`}
      >
        {label}
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className={`w-3 h-3 transition-transform ${active ? 'opacity-100' : 'opacity-30'} ${active && dir === 'asc' ? 'rotate-180' : ''}`}>
          <path fillRule="evenodd" d="M4.22 6.22a.75.75 0 0 1 1.06 0L8 8.94l2.72-2.72a.75.75 0 1 1 1.06 1.06l-3.25 3.25a.75.75 0 0 1-1.06 0L4.22 7.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
        </svg>
      </button>
    </th>
  );
}

export default AdminUsers;
